import { defineComponent, ref, Ref, computed } from 'vue';
import {
    HoursEntryModel, HoursExceptionModel, HoursOfOperationHolidaysCondition, HoursOfOperationType, ListingLocation, IHoursOfOperationManager,
    UpdateHoursLocationsModel, UpdateHoursExceptionsLocationsModel, UpdateHolidaysLocationsModel
} from '@truckdown/locations';
import weeklySchedule from '../EditHours/WeeklySchedule/WeeklySchedule.vue';
import hoursExceptions from '../EditHours/HoursExceptions/HoursExceptions.vue';
import holidayHours from '../EditHours/HolidayHours/HolidayHours.vue';
import hoursTableNavigation from '../EditHours/HoursTableNavigation/HoursTableNavigation.vue';
import hoursSettings from '../EditHours/HoursSettings/HoursSettings.vue';
import listingLookup from '../../ListingLookup/ListingLookup.vue';
import context from '@truckdown/components-systems';

import {
    prepareApiEntriesFromUi, getAfterHoursType, mergeMidnightEntriesFromApi, showMessage
} from '../../utilities';

enum HoursTab {
    Hours = 1,
    Exceptions = 2,
    HolidayHours = 3
}

interface ListingInfo {
    id: string;
    name: string;
    location: ListingLocation;
}

interface IData {
    exceptionsRef: Ref<InstanceType<typeof hoursExceptions>>,
    model: Ref<any>,
    exceptionsModel: Ref<any>,
    holidaysModel: Ref<any>,
    allEntries: Ref<{ [key: string]: HoursEntryModel }>,
    allExceptions: Ref<HoursExceptionModel[]>,
    showAddException: Ref<boolean>,
    tab: Ref<HoursTab>,
    listingsOption: Ref<string>,
    selectedRegion: Ref<string | undefined>,
    availableRegions: Ref<string[]>,
    listingId: Ref<string>,
    isValid: Ref<boolean>,
    errorMessage: Ref<string>,
    countryCode: Ref<string>
}

function getListingIds(listings: ListingInfo[]): string[] {
    return listings.map((l: ListingInfo) => l.id.toString());
}

function getListingIdsByRegion(listings: ListingInfo[], regionCode: string | undefined): string[] {
    if (regionCode) {
            return listings
        .filter((l: ListingInfo) => l.location?.regionCode === regionCode)
        .map((l: ListingInfo) => l.id.toString());
    } else {
        return [];
    }
}

function getCountryCodeByRegion(listings: ListingInfo[], regionCode: string | undefined): string {
    if (regionCode) {
       let listingsFiltered = listings
            .filter((l: ListingInfo) => l.location?.regionCode === regionCode)
            return listingsFiltered[0]?.location?.countryCode ?? ""
    } else {
        return ""
    }
}

const getInitialData = function (props: any): IData {
    function getRegionCodes(listings: ListingInfo[]): string[] {
        return Array.from(
            new Set(
                listings
                    .map(l => l.location?.regionCode)
                    .filter((code): code is string => !!code)
            )
        ).sort();
    }


    return {
        exceptionsRef: ref<InstanceType<typeof hoursExceptions> | null>(null),
        model: ref<UpdateHoursLocationsModel>({
            accountId: props.accountId,
            locationIds: getListingIds(props.listings),
            type: HoursOfOperationType.SelectHours,
            majorHolidaysCondition: HoursOfOperationHolidaysCondition.RegularHours,
            entries: {}
        }),
        exceptionsModel: ref<UpdateHoursExceptionsLocationsModel>({
            accountId: props.accountId,
            locationIds: getListingIds(props.listings),
            exceptions: []
        }),
        holidaysModel: ref<UpdateHolidaysLocationsModel>({
            accountId: props.accountId,
            locationIds: getListingIds(props.listings),
            holidays: {}
        }),
        allEntries: ref<{ [key: string]: HoursEntryModel }>({}),
        allExceptions: ref<HoursExceptionModel[]>([]),
        showAddException: ref<boolean>(false),
        tab: ref<HoursTab>(HoursTab.Hours),
        listingsOption: ref<string>('all'),
        listingId: ref<string>(""),
        availableRegions: ref<string[]>(getRegionCodes(props.listings)),
        selectedRegion: ref<string | undefined>(undefined),
        isValid: ref<boolean>(true),
        errorMessage: ref<string>(""),
        countryCode: ref<string>("")
    };
}

const getComputed = function (props: any) {
    return {
        afterHoursType: computed(() => {
            return getAfterHoursType(props.listing);
        })
    };
}

const getMethods = function (data: IData, props: any, ctx: any) {
    async function saveHours(manager: IHoursOfOperationManager): Promise<boolean> {
            const result = await manager.updateHoursLocations(data.model.value);
            if (result && result.ok) {
                return true;
            };
            data.errorMessage.value = result?.message || "An error occurred while updating hours of operation.";
            return false;
    }

    async function saveHolidays(manager: IHoursOfOperationManager): Promise<boolean> {
        const result = await manager.updateHolidaysLocations(data.holidaysModel.value);
        if (result && result.ok) {
            return true;
        };
        data.errorMessage.value = result?.message || "An error occurred while updating holiday hours.";
        return false;
    }

    async function saveExceptions(manager: IHoursOfOperationManager): Promise<boolean> {
        if (data.exceptionsRef.value) {
            data.exceptionsModel.value.exceptions = data.exceptionsRef.value.getExceptions();
            const result = await manager.updateHoursExceptionsLocations(data.exceptionsModel.value);
            if (result && result.ok) {
                return true;

            };
            data.errorMessage.value = result?.message || "An error occurred while updating  hours.";
            return false;
        }
     return false;
    }

    function updateLocationIds(locationIds: string[]) {
        data.holidaysModel.value.locationIds = locationIds;
        data.model.value.locationIds = locationIds;
        data.exceptionsModel.value.locationIds = locationIds;
    }

    return {
        updateNavigation: function (tab: HoursTab, buttonState: boolean) {
            data.tab.value = tab;
            data.showAddException.value = buttonState;
        },
        updateListingsOption: function () {
            let locationIds = [];
            switch (data.listingsOption.value) {
                case "region":
                    locationIds = getListingIdsByRegion(props.listings, data.selectedRegion.value);
                    data.countryCode.value = getCountryCodeByRegion(props.listings, data.selectedRegion.value)
                    break;
                case "single":
                    locationIds = [data.listingId.value.toString()];
                   
                    break;
                default:
                    locationIds = getListingIds(props.listings);
                    data.countryCode.value = "";
            }
            updateLocationIds(locationIds)
        },
        updateModel: function (model: any) {
            data.model.value.type = model.type;
           
            if (data.model.value.type == HoursOfOperationType.SelectHours) {
                data.tab.value = HoursTab.Hours;
            }
            if (data.model.value.majorHolidaysCondition != model.majorHolidaysCondition) {
                data.tab.value = HoursTab.HolidayHours;
            }
            data.model.value.majorHolidaysCondition = model.majorHolidaysCondition;
            data.isValid.value = true;
        },
        updateEntries: function (entries: { [key: string]: HoursEntryModel }, isValid: boolean) {
            data.model.value.entries = prepareApiEntriesFromUi(entries);
            data.isValid.value = isValid;
        },
        updateExceptions: function (exceptions: HoursExceptionModel[], isValid: boolean) {
            data.exceptionsModel.value.exceptions = exceptions;
            data.showAddException.value = false;
            data.isValid.value = isValid;
        },
        updateHolidays: function (holidays: any) {
            data.holidaysModel.value.holidays = holidays;
            ctx.emit("update-holidays-model", data.holidaysModel.value);
            data.isValid.value = true;
        },
        cancelUpdate: function () {
            data.showAddException.value = false;
        },
        selectListing: function (listing: any) {
            if (listing?.value) {
                data.countryCode.value = listing.terms ?? data.countryCode.value;
                data.listingId.value = listing.value;
                updateLocationIds([listing.value]);
                if (listing.hours != null && listing.hours != undefined) {
                    data.model.value.type = listing.hours.type;
                    data.model.value.majorHolidaysCondition = listing.hours.majorHolidayCondition;
                    data.allEntries.value = mergeMidnightEntriesFromApi(listing.hours.entries || {});
                    data.model.value.entries = listing.hours.entries || {};
                    data.holidaysModel.value.holidays = listing.hours.holidays || {};
                    data.exceptionsModel.value.exceptions = listing.hours.exceptions || [];
                }
            }
        }, 
       
        saveHours: async function () {
            const hoursManager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
            if (data.isValid.value) {
                const hoursOk = await saveHours(hoursManager);
                if (hoursOk) {
                    showMessage(`Hours of operations edits were successfully submitted.`, "success", true, true);
                } else { return }
            }
            
        },
        saveHolidays: async function () {
            const hoursManager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
            if (data.isValid.value) {
                const holidaysOk = await saveHolidays(hoursManager);
                if (holidaysOk) {
                    showMessage(`Holiday Hours edits were successfully submitted.`, "success", true, true);
                } else { return }
            }
        },
        saveExceptions: async function () {
            const hoursManager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
            if (data.isValid.value) {
                const exceptionsOk = await saveExceptions(hoursManager);
                if (exceptionsOk) {
                    showMessage(`Exceptions edits were successfully submitted.`, "success", true, true);
                } else { return }
            }
        },
    };
}

export default defineComponent({
    props: {
        accountId: String,
        listings: {
            type: Array as () => ListingInfo[],
            required: true,
            default: []
        },
        singleEdit: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    emits: ['update-model', 'update-holidays-model', 'update-exceptions-model'],
    components: {
        'listing-lookup': listingLookup,
        'weekly-schedule': weeklySchedule,
        'hours-exceptions': hoursExceptions,
        'holiday-hours': holidayHours,
        'hours-settings': hoursSettings,
        'hours-table-navigation': hoursTableNavigation
    },
    setup: function (props, ctx) {
        var data = getInitialData(props);
        var methods = getMethods(data, props, ctx);
        var computed = getComputed(props);

        return {
            ...data,
            ...methods,
            ...computed,
            'HoursOfOperationType': HoursOfOperationType,
            'HoursOfOperationHolidaysCondition': HoursOfOperationHolidaysCondition,
            'HoursTab': HoursTab
        }
    }
});

